// Template settings modal, mirroring TripSettingsDialog. A template has no trip-wide
// preferences (no staging opt-in, no proxy completion) — for now this only holds the
// template's color, the one setting a template already has (issue #188's color chip,
// moved out of the header and into a settings popup for parity with the trip page).

import type { CSSProperties, ReactNode } from "react";
import { ColorPicker, Dialog, EmojiPicker, vars, type TripColor } from "@pack-it-up/ui-web";

const hintStyle: CSSProperties = {
  fontSize: vars.font.sizeXs,
  color: vars.color.textMuted,
  marginTop: vars.space.xs,
};

const sectionLabelStyle: CSSProperties = {
  fontSize: vars.font.sizeSm,
  fontWeight: vars.font.weightMedium,
  color: vars.color.text,
  marginBottom: vars.space.sm,
};

/** Template color-coding (rows imported from it highlight in this color). */
export interface TemplateColorSetting {
  value: TripColor | undefined;
  onChange: (value: TripColor) => void;
  /** Only owners/editors may change it; viewers see it disabled. */
  canEdit: boolean;
}

/** The template card's emoji glyph. */
export interface TemplateEmojiSetting {
  value: string;
  onChange: (value: string) => void;
  /** Only owners/editors may change it; viewers see it disabled. */
  canEdit: boolean;
}

export interface TemplateSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  templateColor: TemplateColorSetting;
  templateEmoji: TemplateEmojiSetting;
}

export const TemplateSettingsDialog = ({
  open,
  onClose,
  templateColor,
  templateEmoji,
}: TemplateSettingsDialogProps): ReactNode => (
  <Dialog open={open} onClose={onClose} title="Template settings" compact>
    <div style={{ marginBottom: vars.space.lg }}>
      <div style={sectionLabelStyle}>Template icon</div>
      <EmojiPicker value={templateEmoji.value} onChange={templateEmoji.onChange} canEdit={templateEmoji.canEdit} />
    </div>
    <div>
      <div style={sectionLabelStyle}>Template color</div>
      <ColorPicker
        value={templateColor.value}
        onChange={templateColor.onChange}
        canEdit={templateColor.canEdit}
        label="Template color"
      />
      <div style={hintStyle}>
        Rows imported from this template can highlight in this color, instead of tagging.
      </div>
    </div>
  </Dialog>
);
